// Web Push helper
// flow: login → initPush()
//   - ถ้าเคยอนุญาตแล้ว (granted) → subscribe เงียบ ๆ
//   - ถ้ายังไม่ตัดสินใจ (default) → ให้ PushPrompt เด้ง popup ก่อน, กด "ยอมรับ" ค่อยเรียก subscribeNow()
import * as qs from 'qs';
import { writable } from 'svelte/store';
import configs from './config';

const SNOOZE_KEY = 'push_prompt_snooze';
const SNOOZE_UID_KEY = 'push_prompt_uid';  // snooze เป็นของ user ไหน
const SNOOZE_DAYS = 1;        // กด "ไว้ก่อน" แล้วเว้นไปกี่วันค่อยถามใหม่
let busy = false;

// ดึง id ของ member จาก JWT (payload.id) ไว้ผูก snooze กับตัว user
function currentUid() {
  try {
    const t = window.localStorage.getItem('access_token');
    if (!t || t.indexOf('.') === -1) return '';
    const b64 = t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const p = JSON.parse(atob(b64));
    return String(p.id || p._id || p.member_username || '');
  } catch (e) { return ''; }
}

// trigger ให้ PushPrompt เด้งหลัง login สำเร็จ (Auth.svelte เรียก triggerPush())
export const pushTrigger = writable(0);
export function triggerPush() {
  pushTrigger.update((n) => n + 1);
}

// สั่งเปิด popup จากปุ่มในแถบ footer (Footer.svelte เรียก openPushPopup())
export const pushOpen = writable(0);
export function openPushPopup() {
  pushOpen.update((n) => n + 1);
}

// ปุ่ม "แจ้งเตือน" ควรโชว์ไหม (รองรับ push + login + ยังไม่เคยตัดสินใจ)
export function canAskPush() {
  return supported() && !!loggedIn() && webPushEnabled() && Notification.permission === 'default';
}

function supported() {
  return ('serviceWorker' in navigator) && ('PushManager' in window) && ('Notification' in window);
}
function loggedIn() {
  const t = window.localStorage.getItem('access_token');
  return t && t !== '' && window.localStorage.getItem('appid');
}
// เว็บนี้เปิดใช้งาน push ไหม (ตั้งจาก bo setting/general → cloud/configs → localStorage 'setting'.push.enabled)
export function webPushEnabled() {
  try {
    const s = JSON.parse(window.localStorage.getItem('setting') || '{}');
    return !!(s && s.push && s.push.enabled);
  } catch (e) { return false; }
}

// ควรเด้ง popup ขออนุญาตไหม (ยังไม่เคยตัดสินใจ + ไม่ได้กดไว้ก่อนเร็ว ๆ นี้)
export function shouldPrompt() {
  if (!supported() || !loggedIn()) return false;
  if (!webPushEnabled()) return false; // เว็บปิด push ไว้
  if (Notification.permission !== 'default') return false; // granted = subscribe เงียบ ๆ, denied = ไม่ตื๊อ
  const sn = Number(window.localStorage.getItem(SNOOZE_KEY) || 0);
  if (sn && Date.now() < sn) return false;
  return true;
}

export function snoozePrompt() {
  window.localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 86400000));
  window.localStorage.setItem(SNOOZE_UID_KEY, currentUid());
}

// เรียกตอน login: ล้าง snooze เฉพาะเมื่อเป็นคนละ user กับที่กด "ไว้ก่อน" ไว้
// (กันค่าค้างของ user เดิมที่ไม่ได้ logout บล็อก user ใหม่ แต่ user เดิม re-login จะไม่เด้งซ้ำ)
export function resetSnoozeOnLogin() {
  const owner = window.localStorage.getItem(SNOOZE_UID_KEY) || '';
  if (owner !== currentUid()) {
    window.localStorage.removeItem(SNOOZE_KEY);
    window.localStorage.removeItem(SNOOZE_UID_KEY);
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

// core: register SW → (ขอ permission ถ้ายัง) → subscribe → ส่งไป backend
// เรียกหลังผู้ใช้กด "ยอมรับ" (user gesture) หรือกรณีเคย granted แล้ว
export async function subscribeNow() {
  if (busy) return false;
  if (!supported() || !loggedIn() || !webPushEnabled()) return false;

  busy = true;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    // iOS: Web Push ใช้ได้เฉพาะตอนติดตั้ง PWA (standalone) แล้วเท่านั้น
    let perm = Notification.permission;
    if (perm === 'default') perm = await Notification.requestPermission();
    if (perm !== 'granted') { busy = false; return false; }

    const token = window.localStorage.getItem('access_token');
    const appid = window.localStorage.getItem('appid');

    const vres = await fetch(configs.cloudapi + '/push/vapidkey', {
      method: 'GET',
      headers: { 'x-api-appid': appid },
    });
    const vjson = await vres.json();
    const pub = vjson && vjson.payload && vjson.payload.publicKey;
    if (!pub) { busy = false; return false; }

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(pub),
      });
    }

    const j = sub.toJSON();
    await fetch(configs.cloudapi + '/push/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: 'Bearer ' + token,
        'x-api-appid': appid,
      },
      body: qs.stringify({
        endpoint: j.endpoint,
        p256dh: j.keys.p256dh,
        auth: j.keys.auth,
      }),
    });
    busy = false;
    return true;
  } catch (e) {
    busy = false;
    return false;
  }
}

// เรียกตอน login: ถ้าเคยอนุญาตแล้ว → subscribe เงียบ ๆ (ไม่เด้ง popup)
export async function initPush() {
  if (!supported() || !loggedIn() || !webPushEnabled()) return;
  if (Notification.permission === 'granted') subscribeNow();
}

// เปิดแอปแบบ standalone (= ติดตั้ง PWA แล้ว)
function isStandaloneMode() {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  } catch (e) { return false; }
}

// standalone + ล็อกอินอยู่ → บันทึกการติดตั้ง PWA (สำหรับ report ใน BO) — แยกจากระบบ push
export async function reportInstall() {
  try {
    if (!isStandaloneMode() || !loggedIn()) return;
    const token = window.localStorage.getItem('access_token');
    const appid = window.localStorage.getItem('appid');
    await fetch(configs.cloudapi + '/push/installed', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: 'Bearer ' + token,
        'x-api-appid': appid,
      },
      body: qs.stringify({ theme: 'member_v4' }),
    });
  } catch (e) { /* เงียบ */ }
}
